import { FC, useCallback, useEffect, useState } from "react";

import { useBlocNext } from "@blac/react";
import { Button } from "@9amhealth/shared";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import { fileState, tracker } from "src/state/state";
import translate from "src/lib/translate";
import useEscapeKey from "src/ui/hooks/useEscapeKey";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import Translate from "src/ui/components/Translate/Translate";

import Feedback from "./Feedback";
import MealInsightActionButton from "./MealInsightActionButton";
import MealInsightBloc from "./MealInsightBloc";
import MealInsightContent from "./MealInsightContent";

const MealInsightOverview: FC = () => {
  const [
    { mealInsight, error, loading, imageError, uploadProgress, previewUrl },
    {
      generateMealInsight,
      clearMealInsight,
      discardMealInsight,
      rateMealInsight
    }
  ] = useBlocNext(MealInsightBloc);
  const [, { invalidate: invalidateMeals }] = useBlocNext(MealInsightsCubit);
  const params = useParams();
  const { id } = params;
  const [searchParams] = useSearchParams();
  const fromMeals = searchParams.get("from") === "meals";
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      clearMealInsight();
      if (id && id !== Path.tmp) {
        void generateMealInsight(id);
        if (!previewUrl) {
          const maxWidth = 500;
          const windowWidth = window.innerWidth;
          const density = window.devicePixelRatio;
          const width = Math.round(Math.min(windowWidth, maxWidth) * density);
          void fileState.loadPreview(id, width, width).then(setImageUrl);
        }
      }
    }
  }, [id, generateMealInsight, previewUrl, clearMealInsight]);

  useEffect(() => {
    if (mealInsight?.insights) {
      tracker.track("Meal insight information displayed");
    }
  }, [mealInsight?.insights]);

  useEffect(() => {
    if (imageError || error) {
      navigate(createAppPath(Path.home));
    }
  }, [imageError, error, navigate]);

  useEffect(() => {
    const displayImageUrl = imageUrl || previewUrl;
    if (displayImageUrl) {
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = displayImageUrl;
    }
  }, [imageUrl, previewUrl]);

  const navigateToHome = useCallback(() => {
    invalidateMeals();
    navigate(createAppPath(Path.home));
  }, [navigate, invalidateMeals]);

  const goToMealsAndReload = useCallback(() => {
    invalidateMeals();
    navigate(createAppPath(Path.meals));
  }, [navigate, invalidateMeals]);

  const exitToOrigin = useCallback(() => {
    if (fromMeals) {
      goToMealsAndReload();
    } else {
      navigateToHome();
    }
  }, [fromMeals, goToMealsAndReload, navigateToHome]);

  const handleDiscard = useCallback(async () => {
    const discarded = await discardMealInsight();
    if (discarded) {
      exitToOrigin();
    }
  }, [discardMealInsight, exitToOrigin]);

  const handleConfirmDiscard = useCallback(async () => {
    setConfirmDiscardOpen(false);
    await handleDiscard();
  }, [handleDiscard]);

  const handleClose = useCallback(() => {
    setConfirmDiscardOpen(true);
  }, []);

  useEscapeKey(handleClose);

  const insights = mealInsight?.insights;
  const isLoading = loading || !mealInsight;
  const displayImageUrl = imageUrl || previewUrl;
  const mealIdentified = insights?.mealIdentified !== false;

  return (
    <>
      <MealInsightContent
        imageUrl={displayImageUrl}
        imageLoaded={imageLoaded}
        loading={isLoading}
        mealIdentified={mealIdentified}
        mealName={insights?.mealName}
        caloriesRange={insights?.caloriesRange}
        insightsHtml={insights?.insights}
        nutrients={{
          protein: insights?.protein,
          fiber: insights?.fiber,
          totalFats: insights?.totalFats,
          totalCarbohydrates: insights?.totalCarbohydrates
        }}
        uploadProgress={uploadProgress}
        showCloseButton
        closeContext={fromMeals ? "meals" : "home"}
        onClose={handleClose}
        feedbackSlot={
          mealInsight ? (
            <Feedback onRate={(rating) => void rateMealInsight(rating)} />
          ) : null
        }
        actionSlot={
          mealInsight ? (
            <>
              <MealInsightActionButton
                label={<Translate msg="mealInsights.discard" />}
                onClick={() => void handleDiscard()}
              />
              <Button
                theme="sunrise"
                grow
                hideArrow
                aria-label={translate("mealInsights.save")}
                onPress={exitToOrigin}
              >
                <Translate msg="mealInsights.save" />
              </Button>
            </>
          ) : null
        }
      />
      {confirmDiscardOpen && (
        <AppDialog
          confirmText="mealInsights.discard.confirm"
          cancelText="mealInsights.delete.no"
          confirmArrow={false}
          destructive
          hideClose
          onCancel={() => setConfirmDiscardOpen(false)}
          onConfirm={() => void handleConfirmDiscard()}
        >
          <h5>
            <Translate msg="mealInsights.discard.confirmTitle" />
          </h5>
          <p>
            <Translate msg="mealInsights.discard.confirmBody" />
          </p>
        </AppDialog>
      )}
    </>
  );
};

export default MealInsightOverview;
